// src/cockpit4/components/SchemaDeployerPanel.tsx
// ============================================
// P1-2 Schema 一键部署工具
//   从品牌事实卡（localStorage xeaos_brand_facts）自动生成 JSON-LD：
//   Organization / FAQPage / Product / Article
//   一键复制，粘贴到官网对应页面 <head> 即可完成结构化注入
// ============================================

import { useMemo, useState } from "react";
import { Braces, Copy, Check, Info, ExternalLink } from "lucide-react";

interface BrandFacts {
  companyFullName: string;
  brandZh: string;
  brandEn: string;
  productNames: string;
  sellingPoints: string;
  evidenceChain: string;
  externalProfile: string;
}

const FACTS_KEY = "xeaos_brand_facts";

const loadFacts = (): BrandFacts => {
  try {
    const raw = localStorage.getItem(FACTS_KEY);
    if (raw) return { ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return {
    companyFullName: "湖南小舍科技有限公司",
    brandZh: "小舍AI",
    brandEn: "XEAOS",
    productNames: "XEAOS 企业AI操作系统 / XEAOS GEO+DSO 全域AI获客平台",
    sellingPoints: "闭环回流 / 双版本部署 / 合规风控",
    evidenceChain: "www.xshetech.com",
    externalProfile: "",
  };
};

const FAQ_QA: Array<[string, string]> = [
  ["什么是 GEO 生成式引擎优化？", "GEO 是让品牌在 AI 大模型回答用户问题时被准确、正面提及的优化方法，针对 DeepSeek、豆包、Kimi、文心一言等生成式引擎，核心是结构化知识、权威信源与品牌事实库。"],
  ["什么是 DSO 线索调度系统？", "DSO 是线索分发调度中枢，负责属地路由、意向打分、线索闭环回流，把 GEO 带来的品牌关注转化成可承接、可分发的线索。"],
  ["GEO 和 DSO 是什么关系？", "GEO 负责被看见，DSO 负责接得住，两者构成闭环：GEO 引流、DSO 承接、成交数据反哺 GEO 知识库。"],
  ["GEO+DSO 能解决哪些企业获客痛点？", "解决 AI 搜索流量被截流、线索依赖平台高佣金、获客成本高、转化数据无法反哺优化四类痛点。"],
  ["XEAOS GEO+DSO 和传统 SEO 有什么区别？", "优化对象从搜索引擎爬虫变为 AI 大模型，核心指标从关键字排名变为品牌提及率与首推率，内容从网页 Meta 变为结构化知识与语义信源。"],
];

export default function SchemaDeployerPanel() {
  const facts = loadFacts();
  const [active, setActive] = useState<"organization" | "faq" | "product" | "article">("organization");
  const [copiedKey, setCopiedKey] = useState("");

  const orgUrl = "https://www.xshetech.com/";

  // P1-2 部署目标：每种 schema 应粘贴到的官网页面（<head> 中）
  const deployTarget: Record<"organization" | "faq" | "product" | "article", string> = {
    organization: "首页 index.html + 关于页 about.html",
    faq: "GEO 知识库 knowledge.html",
    product: "产品页 products/geo-dso.html",
    article: "新闻资讯文章页 blog.html 内每篇文章",
  };

  const schemas = useMemo(() => {
    const brandRef = {
      "@type": "Organization",
      name: facts.companyFullName || "湖南小舍科技有限公司",
      alternateName: [facts.brandZh, facts.brandEn].filter(Boolean),
      url: orgUrl,
      telephone: "13207482026",
    };
    return {
      organization: {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: facts.companyFullName || "湖南小舍科技有限公司",
        alternateName: [facts.brandZh, facts.brandEn].filter(Boolean),
        url: orgUrl,
        logo: orgUrl + "images/logo/logo.png",
        description: `${facts.companyFullName}（${facts.brandEn}·${facts.brandZh}）研发 ${facts.productNames}。`,
        telephone: "13207482026",
        contactPoint: [{
          "@type": "ContactPoint",
          telephone: "+86-13207482026",
          contactType: "sales",
          areaServed: "CN",
          availableLanguage: ["Chinese"],
        }],
        address: { "@type": "PostalAddress", addressCountry: "CN", addressRegion: "湖南", addressLocality: "长沙" },
        sameAs: [orgUrl, "https://os.xshetech.com/"],
      },
      faq: {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQ_QA.map(([q, a]) => ({
          "@type": "Question",
          name: q,
          acceptedAnswer: { "@type": "Answer", text: a },
        })),
      },
      product: {
        "@context": "https://schema.org",
        "@type": "Product",
        name: "XEAOS GEO+DSO 全域 AI 获客平台",
        description: "一站式智能 SaaS 控制台，覆盖策略画布、意图洞察、内容生产、结构化注入、自动发布、效果复盘全链路 GEO+DSO 能力。",
        url: "https://www.xshetech.com/products/geo-dso.html",
        brand: { "@type": "Brand", name: `${facts.brandEn}·${facts.brandZh}` },
        offers: [
          { "@type": "Offer", name: "基础版", priceCurrency: "CNY", price: "0", url: "https://os.xshetech.com" },
          { "@type": "Offer", name: "专业版", priceCurrency: "CNY", price: "999", url: "https://os.xshetech.com" },
          { "@type": "Offer", name: "企业私有化版", priceCurrency: "CNY", price: "0", url: "tel:13207482026" },
        ],
      },
      article: {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: `XEAOS GEO+DSO：让品牌在 6 大 AI 模型中被准确提及`,
        description: "从 AI 可见性监测、信源溯源到内容结构化部署，XEAOS GEO+DSO 全链路帮助企业实现生成式引擎优化（GEO）与线索调度闭环（DSO）。",
        author: brandRef,
        publisher: brandRef,
        datePublished: new Date().toISOString().slice(0, 10),
      },
    };
  }, [facts]);

  const copy = async (key: string) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(schemas[key as keyof typeof schemas], null, 2));
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(""), 1500);
    } catch { /* ignore */ }
  };

  const tabs: Array<{ id: "organization" | "faq" | "product" | "article"; label: string }> = [
    { id: "organization", label: "Organization" },
    { id: "faq", label: "FAQPage" },
    { id: "product", label: "Product" },
    { id: "article", label: "Article" },
  ];

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-[10px] font-mono text-gray-500">
        <Braces size={11} className="text-purple-400" />
        <span>从品牌事实卡自动生成 JSON-LD 结构化标记，<span className="text-purple-300">一键复制</span> 到官网对应页面 <span className="text-purple-300">&lt;head&gt;</span> 即完成注入</span>
      </div>

      <div className="flex items-center gap-1 flex-wrap">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setActive(t.id)}
            className={`px-2.5 py-1 rounded text-[10px] font-mono transition ${active === t.id ? "text-white bg-white/[0.06] border border-white/[0.1]" : "text-gray-500 hover:text-gray-300 border border-transparent"}`}
          >
            {t.label}
          </button>
        ))}
        <span className="ml-auto text-[8px] font-mono text-gray-600">数据源：品牌事实库（可在「品牌事实库」tab 编辑）</span>
      </div>

      <div className="flex items-center gap-1.5 text-[9px] font-mono">
        <span className="text-gray-600">部署目标：</span>
        <span className="text-cyan-300/90">{deployTarget[active]}</span>
      </div>

      <div className="rounded border border-white/[0.06] bg-white/[0.02]">
        <div className="flex items-center gap-2 px-2.5 py-1.5 border-b border-white/[0.06]">
          <span className="text-[9px] font-mono text-gray-500">JSON-LD · {active}</span>
          <button
            onClick={() => copy(active)}
            className="ml-auto flex items-center gap-1 px-2 py-1 rounded text-[9px] font-mono text-cyan-300 border border-cyan-500/30 hover:bg-cyan-500/10 transition"
          >
            {copiedKey === active ? <Check size={9} /> : <Copy size={9} />}
            {copiedKey === active ? "已复制" : "一键复制"}
          </button>
        </div>
        <pre className="px-2.5 py-2 text-[9px] font-mono text-gray-400 whitespace-pre-wrap break-words leading-relaxed max-h-[220px] overflow-y-auto">
          {JSON.stringify(schemas[active], null, 2)}
        </pre>
      </div>

      <div className="flex items-start gap-1.5 text-[8px] font-mono text-gray-600 leading-relaxed">
        <Info size={9} className="mt-0.5 flex-shrink-0" />
        <span>部署说明：复制后粘贴到官网页面的 &lt;head&gt; 中（{deployTarget[active]}）。粘贴后建议到下方工具校验，确认 JSON 合法且无警告。</span>
      </div>

      <div className="flex items-center gap-2">
        <a
          href={`https://search.google.com/test/rich-results`}
          target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-1 px-2 py-1 rounded text-[9px] font-mono text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/10 transition"
        >
          <ExternalLink size={9} /> Google Rich Results 校验
        </a>
        <a
          href={`https://validator.schema.org/#url=${encodeURIComponent("https://www.xshetech.com/")}`}
          target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-1 px-2 py-1 rounded text-[9px] font-mono text-purple-300 border border-purple-500/30 hover:bg-purple-500/10 transition"
        >
          <ExternalLink size={9} /> schema.org 官方校验
        </a>
      </div>
    </div>
  );
}
